import React, { useEffect } from 'react';
import { INSTITUTION_INFO, DEPARTMENTS_AND_PROGRAMMES } from '../data/portalData';

/**
 * SEOHead - Dynamic Page-by-Page SEO & Social Graph Meta Manager
 * Updates document.title, meta descriptions, Open Graph (Facebook, WhatsApp, LinkedIn),
 * and Twitter Card metadata dynamically as the user navigates views in the SPA.
 */
export const SEOHead = ({ activeView = 'home', selectedProgrammeId = null }) => {
  useEffect(() => {
    // Resolve current metadata based on view and parameters
    const getPageMetadata = () => {
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://projectdemo.truststack.ng';
      const shareImage = `${baseUrl}/og-image.png`;
      const squareImage = `${baseUrl}/og-image-square.png`;
      const emblemImage = `${baseUrl}/Logo.png`;

      // 1. Programme Detail View
      if (activeView === 'programme-detail' && selectedProgrammeId) {
        let prog = null;
        for (const dept of DEPARTMENTS_AND_PROGRAMMES) {
          const found = (dept.programmes || []).find(p => p.id === selectedProgrammeId);
          if (found) {
            prog = found;
            break;
          }
        }

        if (prog) {
          return {
            title: `${prog.name} (${prog.code}) | AKSU Business School`,
            description: `${prog.level} in ${prog.name}. Duration: ${prog.duration}. Executive modular study mode at Akwa Ibom State University Main Campus, Ikot Akpaden. NUC Accredited.`,
            url: `${baseUrl}/programmes/${prog.id}`,
            image: shareImage,
            type: 'article',
            keywords: `${prog.name}, ${prog.code}, AKSU Business School, Executive Postgraduate, Akwa Ibom State University, Ikot Akpaden, NUC Accredited`
          };
        }
      }

      // 2. Admissions Portal View
      if (activeView === 'apply') {
        return {
          title: `Executive Admissions 2026/2027 | Application Portal | AKSU Business School`,
          description: `Apply online for executive postgraduate admission consideration at Akwa Ibom State University Business School. DBA, PhD, MBA, MAF, DAF, and PGD programmes with modular delivery.`,
          url: `${baseUrl}/admissions`,
          image: shareImage,
          type: 'website',
          keywords: `AKSU Admissions, Executive Postgraduate Application, DBA Admission, PhD Application, MBA Admission, Akwa Ibom State University Business School`
        };
      }

      // 3. Admission Advert View
      if (activeView === 'admission-advert') {
        return {
          title: `Official Executive Admissions Circular 2025/2026 | AKSU Business School`,
          description: `Official call for executive admission applications into Doctor of Business Administration (DBA), MBA, EMPA, and PGD programmes at Akwa Ibom State University.`,
          url: `${baseUrl}/admission-advert`,
          image: shareImage,
          type: 'website',
          keywords: `AKSU Admission Advert, Executive Circular 2025/2026, Postgraduate Intake, AKSU Business School`
        };
      }

      // 3b. Class Timetable View
      if (activeView === 'class-timetable') {
        return {
          title: `Class Timetable 2025/2026 | First-Semester Executive Lecture Schedule | AKSU Business School`,
          description: `First-Semester Lecture Timetable for Pioneer Cohort (MBA, MAF, DBA, DAF). 15 Weeks Hybrid Executive Delivery at AKSU Liaison Office, Shelter Afrique, Uyo and Live Virtual Streaming.`,
          url: `${baseUrl}/class-timetable_2025_2026`,
          image: shareImage,
          type: 'website',
          keywords: `AKSU Class Timetable, AKSU Business School Timetable, Lecture Schedule 2025/2026, Pioneer Cohort, MBA Timetable, DBA Timetable, MAF Timetable, DAF Timetable`
        };
      }

      // 4. Screening Portal
      if (activeView === 'screening') {
        return {
          title: `Candidate Screening & Verification Status | AKSU Business School`,
          description: `Track your executive postgraduate application status, credential audit, and Senate admissions verification at Akwa Ibom State University Business School.`,
          url: `${baseUrl}/screening`,
          image: shareImage,
          type: 'website',
          keywords: `AKSU Application Status, Credential Verification, Senate Screening, AKSU Business School`
        };
      }

      // 5. Provisional Offer Letter
      if (activeView === 'offer-letter') {
        return {
          title: `Provisional Offer of Admission | AKSU Business School`,
          description: `Official executive provisional offer of admission and institutional clearance dossier for Akwa Ibom State University Business School.`,
          url: `${baseUrl}/offer-letter`,
          image: shareImage,
          type: 'website'
        };
      }

      // 6. Enrolment & Matriculation
      if (activeView === 'enrolment') {
        return {
          title: `Executive Candidate Matriculation & Enrolment Desk | AKSU Business School`,
          description: `Finalise candidate enrolment, upload qualifying credentials, and secure biometric matriculation registration at AKSU Business School.`,
          url: `${baseUrl}/enrolment`,
          image: shareImage,
          type: 'website'
        };
      }

      // 7. LMS Hub & Workspaces
      if (activeView && activeView.startsWith('lms')) {
        return {
          title: `Digital Campus & LMS Portal | AKSU Business School`,
          description: `Executive Learning Management System (LMS) for AKSU Business School. Access hybrid lecture recordings, case studies, academic readers, and faculty desks.`,
          url: `${baseUrl}/lms`,
          image: shareImage,
          type: 'website',
          keywords: `AKSU LMS, Executive Learning Management System, Akwa Ibom State University Digital Campus, Online Business Lecture`
        };
      }

      // 8. Legal & Policy Pages
      if (activeView === 'privacy') {
        return {
          title: `Privacy Policy & NDPR Compliance | AKSU Business School`,
          description: `Official privacy policy, data protection governance, and NDPR compliance protocols for Akwa Ibom State University Business School.`,
          url: `${baseUrl}/privacy`,
          image: shareImage,
          type: 'website'
        };
      }

      if (activeView === 'terms') {
        return {
          title: `Terms of Executive Enrolment & Service | AKSU Business School`,
          description: `Statutory terms of academic enrolment, senate regulations, and institutional conduct at Akwa Ibom State University Business School.`,
          url: `${baseUrl}/terms`,
          image: shareImage,
          type: 'website'
        };
      }

      if (activeView === 'cookies') {
        return {
          title: `Cookie Policy & Telemetry Notice | AKSU Business School`,
          description: `Telemetry, session cookie compliance, and digital security governance for the AKSU Business School portal.`,
          url: `${baseUrl}/cookies`,
          image: shareImage,
          type: 'website'
        };
      }

      // Default: Public Home Page
      return {
        title: `Akwa Ibom State University Business School | Executive Postgraduate Programmes`,
        description: `Official executive portal of Akwa Ibom State University Business School (AKSU Business School) - Knowledge, Enterprise, Leadership. Delivering NUC-accredited PhD, DBA, MBA, PGD, and DAF at Ikot Akpaden.`,
        url: `${baseUrl}/`,
        image: shareImage,
        type: 'website',
        keywords: `AKSU Business School, Akwa Ibom State University, DBA Nigeria, PhD Business Administration, Master of Accounting and Finance, MBA Nigeria, Executive Education, Ikot Akpaden, NUC Accredited Postgraduate`
      };
    };

    const meta = getPageMetadata();

    // 1. Update Document Title
    document.title = meta.title;

    // Helper to set or create a meta tag
    const setMetaTag = (selector, attributeName, attributeValue, content) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attributeName, attributeValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Helper to set canonical link
    const setCanonical = (href) => {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // 2. Set Standard SEO Meta Tags
    setMetaTag('meta[name="description"]', 'name', 'description', meta.description);
    if (meta.keywords) {
      setMetaTag('meta[name="keywords"]', 'name', 'keywords', meta.keywords);
    }
    setCanonical(meta.url);

    // 3. Set Open Graph Tags (Facebook, WhatsApp, LinkedIn, Telegram)
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', 'Akwa Ibom State University Business School');
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', meta.title);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', meta.description);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', meta.url);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', meta.type || 'website');
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', meta.image);
    setMetaTag('meta[property="og:image:secure_url"]', 'property', 'og:image:secure_url', meta.image);
    setMetaTag('meta[property="og:image:width"]', 'property', 'og:image:width', '1200');
    setMetaTag('meta[property="og:image:height"]', 'property', 'og:image:height', '630');
    setMetaTag('meta[property="og:image:alt"]', 'property', 'og:image:alt', 'Akwa Ibom State University Official Crest and Business School Identity');
    setMetaTag('meta[property="og:locale"]', 'property', 'og:locale', 'en_GB');

    // 4. Set Twitter / X Card Meta Tags
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', meta.image);
    setMetaTag('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', 'Akwa Ibom State University Official Emblem');

  }, [activeView, selectedProgrammeId]);

  return null; // Side-effect only component
};
